import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Body2, Input, Label, Select, SelectTrigger } from "@activecollab/components";
import classNames from "classnames";
import { Field, useFormikContext } from "formik";
import { isEqual } from "lodash";
import PropTypes from "prop-types";
import { v4 } from "uuid";
import { WorkWeekHours } from "~/components/Settings/WorkWeekHours";
import { UserType } from "~/records/record.user";
import { getConfigOptions } from "~/store/selectors/selector.configOptions";
import { getActiveJobTypesArrayOfIdAndNameSelector } from "~/store/selectors/selector.jobTypes";
import { shortDayNames } from "~/utils/date";
import { validateWorkWeekHours } from "~/utils/utils";

const AddYourTeamFormElements = ({ setNewMembers, fieldRef, focusFirstInvalidField }) => {
  const { t } = useTranslation();
  const [addMemberErrors, setAddMemberErrors] = useState({});
  const [showCustomCapacity, setShowCustomCapacity] = useState(false);

  const configOptions = useSelector(getConfigOptions);
  const jobTypes = useSelector(getActiveJobTypesArrayOfIdAndNameSelector);

  const { setFieldValue, resetForm, setTouched, touched, errors, values } = useFormikContext();

  const userTypes = [
    { id: UserType.OWNER, name: t(`${UserType.OWNER}`) },
    { id: UserType.MEMBER, name: t(`${UserType.MEMBER}`) },
  ];

  const workWeekHoursValidator = useCallback((v) => validateWorkWeekHours(v), []);
  const resolvedTriggerTitle = useCallback(
    (value) => {
      let selectedJobType;
      if (value.length === 1) {
        const [id] = value;
        selectedJobType = jobTypes.find((job) => job.id === id).name;
      }
      return value.length === 0 || value.length === jobTypes.length
        ? t("All")
        : selectedJobType ?? `${value.length} ${t("selected")}`;
    },
    [jobTypes, t]
  );

  const days = useMemo(() => {
    const weeklyWorkerCapacity = configOptions.weekly_worker_capacity;
    const timeWorkdays = configOptions.time_workdays;
    const firstDayOfWeek = configOptions.time_first_week_day;
    const workDays = timeWorkdays.map((day) => {
      return +day;
    });
    if (weeklyWorkerCapacity) {
      const days = weeklyWorkerCapacity?.map((capacity, index) => {
        return {
          label: shortDayNames[index],
          value: !workDays.includes(index) ? 0 : capacity / 60,
          disabled: !workDays.includes(index),
          day: index,
          isValid: workWeekHoursValidator(capacity / 60),
        };
      });
      if (firstDayOfWeek) {
        const sunday = days.shift();
        days.push(sunday);
      }

      return days;
    }
    return [];
  }, [
    configOptions.time_first_week_day,
    configOptions.time_workdays,
    configOptions.weekly_worker_capacity,
    workWeekHoursValidator,
  ]);

  const toggleCapacity = useCallback(() => {
    setShowCustomCapacity((prevState) => !prevState);
  }, []);

  const onWorkWeekChange = useCallback(
    (modifiedDays) => {
      if (
        isEqual(
          modifiedDays.map((day) => day.value),
          days.map((day) => day.value)
        )
      ) {
        return;
      }

      if (
        modifiedDays.some((day) => {
          if (day.disabled) {
            return false;
          }
          return !workWeekHoursValidator(day.value);
        })
      ) {
        return;
      }

      const values = modifiedDays.map((day) => day.value * 60);

      if (configOptions.time_first_week_day) {
        const sunday = values.pop();
        values.unshift(sunday);
      }

      setFieldValue("weekly_worker_capacity", values);
    },
    [configOptions.time_first_week_day, days, setFieldValue, workWeekHoursValidator]
  );

  const handleAddTeamMember = useCallback(async () => {
    const { first_name, last_name, role, job_type_ids, weekly_worker_capacity } = values;
    setAddMemberErrors((prevState) => {
      const { first_name, last_name, ...rest } = prevState;
      return rest;
    });
    const data = {
      first_name,
      last_name,
      name: `${first_name} ${last_name}`,
      role,
      job_type_ids,
      weekly_worker_capacity,
      uuid: v4(),
    };

    if (!first_name.trim() || !last_name.trim()) {
      const errorsData = {
        ...(!first_name.trim() && { first_name: t("First name is required.") }),
        ...(!last_name.trim() && { last_name: t("Last name is required.") }),
      };
      if (Object.values(errorsData)?.length) {
        setAddMemberErrors((prevState) => ({ ...prevState, ...errorsData }));
        return focusFirstInvalidField(errorsData, true);
      }
    }

    if (addMemberErrors?.weekly_worker_capacity) {
      return focusFirstInvalidField(addMemberErrors.weekly_worker_capacity, true);
    }

    setNewMembers((prevState) => [...prevState, data]);
    resetForm();
    setShowCustomCapacity(false);
  }, [addMemberErrors?.weekly_worker_capacity, focusFirstInvalidField, resetForm, setNewMembers, t, values]);

  const validateCapacityValues = useCallback(
    (values) => {
      const hasErrors = values.filter((v) => !v.isValid);
      if (!hasErrors.length) {
        return setAddMemberErrors((prevState) => {
          const { weekly_worker_capacity, ...rest } = prevState;
          return rest;
        });
      }
      const errorsData = hasErrors.reduce((acc, dayValue) => {
        const key = dayValue.label;
        acc[key] = !dayValue.isValid;
        return acc;
      }, {});

      setAddMemberErrors((prevState) => ({ ...prevState, weekly_worker_capacity: errorsData }));
    },
    [setAddMemberErrors]
  );

  return (
    <>
      <div className="tw-flex tw-flex-col sm:tw-flex-row tw-w-full tw-items-center tw-justify-between tw-mb-4 tw-gap-4">
        <Field name="first_name">
          {({ field }) => {
            return (
              <div className="tw-flex tw-w-full tw-flex-col">
                <Label
                  htmlFor="first_name"
                  className="tw-mb-1"
                  variant="Caption 1"
                >
                  {t("First Name")}*
                </Label>
                <Input
                  id="first_name"
                  data-testid="first_name"
                  className="tw-w-full tw-shrink-0"
                  value={field.value}
                  onChange={(e) => {
                    setTouched({});
                    setAddMemberErrors((prevState) => {
                      const { first_name, last_name, ...rest } = prevState;
                      return rest;
                    });
                    setFieldValue(field.name, e.target.value);
                  }}
                  maxLength={50}
                  ref={(el) => {
                    if (fieldRef) return (fieldRef.current.first_name = el);
                  }}
                  invalid={(errors.first_name && touched.first_name) || addMemberErrors.first_name}
                />
              </div>
            );
          }}
        </Field>
        <Field name="last_name">
          {({ field }) => {
            return (
              <div className="tw-flex tw-w-full tw-flex-col">
                <Label
                  htmlFor="last_name"
                  className="tw-mb-1"
                  variant="Caption 1"
                >
                  {t("Last Name")}*
                </Label>
                <Input
                  id="last_name"
                  data-testid="last_name"
                  className="tw-w-full tw-shrink-0"
                  value={field.value}
                  onChange={(e) => {
                    setTouched({});
                    setAddMemberErrors((prevState) => {
                      const { first_name, last_name, ...rest } = prevState;
                      return rest;
                    });
                    setFieldValue(field.name, e.target.value);
                  }}
                  maxLength={50}
                  ref={(el) => {
                    if (fieldRef) return (fieldRef.current.last_name = el);
                  }}
                  invalid={(errors.last_name && touched.last_name) || addMemberErrors.last_name}
                />
              </div>
            );
          }}
        </Field>
      </div>
      <div className="tw-flex tw-flex-col sm:tw-flex-row tw-w-full tw-items-center tw-justify-between tw-mb-4 tw-gap-4">
        <Field name="role">
          {({ field }) => {
            return (
              <div className="tw-flex tw-w-full tw-flex-col">
                <Label
                  htmlFor="role"
                  className="tw-mb-1"
                  variant="Caption 1"
                >
                  {t("Role")}
                </Label>
                <Select
                  target={
                    <SelectTrigger
                      data-testid="user-role"
                      className="tw-w-auto"
                    >
                      {field.value}
                    </SelectTrigger>
                  }
                  selected={field.value}
                  options={userTypes}
                  onChange={(v) => setFieldValue(field.name, v)}
                  disableSearch
                  forceCloseMenu
                />
              </div>
            );
          }}
        </Field>
        <Field name="job_type_ids">
          {({ field }) => {
            return (
              <div className="tw-flex tw-w-full tw-flex-col">
                <Label
                  htmlFor="job_type_ids"
                  className="tw-mb-1"
                  variant="Caption 1"
                >
                  {t("Job Types")}
                </Label>
                <Select
                  target={
                    <SelectTrigger
                      data-testid="job-types"
                      className="tw-w-auto"
                    >
                      {resolvedTriggerTitle(field.value)}
                    </SelectTrigger>
                  }
                  selected={field.value}
                  options={jobTypes}
                  onChange={(v) => setFieldValue(field.name, v)}
                  type="multiple"
                  disableSearch
                />
              </div>
            );
          }}
        </Field>
      </div>
      <div className="tw-flex tw-flex-col tw-w-full tw-gap-3 tw-mb-4">
        <div className="tw-flex tw-w-full tw-pt-3 tw-pb-1 tw-items-end">
          <Body2
            className="tw-flex tw-h-5"
            color={`${showCustomCapacity ? "bold" : "secondary"}`}
          >
            {t(`${showCustomCapacity ? "Workweek and Daily Capacity" : "Available for 40 hours a week."}`)}
          </Body2>
          <Body2
            color="theme-primary"
            weight="regular"
            className={classNames("tw-flex tw-cursor-pointer tw-w-fit tw-ml-auto", {
              "tw-ml-2": !showCustomCapacity,
            })}
            onClick={toggleCapacity}
          >
            {t(`${showCustomCapacity ? "Use Default" : "Change"}`)}
          </Body2>
        </div>
        {showCustomCapacity ? (
          <Field name="weekly_worker_capacity">
            {() => (
              <>
                <WorkWeekHours
                  days={days}
                  onBlur={onWorkWeekChange}
                  isValid={workWeekHoursValidator}
                  onChange={validateCapacityValues}
                  fieldRef={fieldRef}
                  className="tw-justify-between"
                />
                <Body2 color="tertiary">
                  {t(
                    "If a team member has a different schedule, enter their adjusted working hours here. Values must end with .00, .25, .5, or .75."
                  )}
                </Body2>
              </>
            )}
          </Field>
        ) : null}
      </div>
      <div className="tw-flex tw-p-1">
        <Body2
          color="theme-primary"
          weight="medium"
          className="tw-cursor-pointer tw-h-6"
          onClick={handleAddTeamMember}
        >
          {t("Add Team Member")}
        </Body2>
      </div>
    </>
  );
};

AddYourTeamFormElements.propTypes = {
  setNewMembers: PropTypes.func,
  fieldRef: PropTypes.object,
  focusFirstInvalidField: PropTypes.func,
};

export default AddYourTeamFormElements;
